<template>
  <div>
    <content-loading v-if="isInitializing" />
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div class="d-flex" style="margin-bottom: 1.70rem;" @click="goBack()">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;">
            <path
              d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
              stroke="#FF7C48"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <div class="d-flex align-items-center" style="margin-left: 1.25rem; gap: 2.34rem;">
            <div>
              <h2 class="mb-1">{{ $t("firm.audit.detail-title") }}</h2>
              <h5 v-if="firm" class="text-muted mb-0">{{ firm.name | translate }}</h5>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center">
          <b-button variant="light" size="sm" class="mr-2" @click="refresh">
            <i class="bx bx-refresh"></i>
            {{ $t("common.refresh") }}
          </b-button>

          <b-button variant="primary" size="sm" class="mr-1" @click="openSendModal" :disabled="isSending || !selectedAudit">
            <b-spinner v-if="isSending" small type="border" class="mr-2" />
            {{ $t("firm.audit.send-question-set") }}
          </b-button>
        </div>
      </div>
      <div v-if="firm" class="mb-4">
        <span class="mr-4">
          <i class="mdi mdi-help text-main"></i>
          <strong>{{ $t("firm.support-service") }}:</strong>
          {{ firm.supportService ? $t("common.yes") : $t("common.no") }}
        </span>
        <span class="mr-4">
          <i class="mdi mdi-server text-main"></i>
          <strong>{{ $t("firm.virtual-server") }}:</strong>
          {{ firm.virtualServer ? $t("common.yes") : $t("common.no") }}
        </span>
        <span class="mr-4">
          <strong>{{ $t("firm.status.text") }}:</strong>
          <firm-status :status="firmStatus" />
        </span>
      </div>

      <div class="table-responsive">
        <table class="table project-list-table compact">
          <thead>
            <tr>
              <th>{{ $t("question-set.name") }}</th>
              <th>{{ $t("firm.audit.status.text") }}</th>
              <th>{{ $t("firm.audit.audit-start") }}</th>
              <th>{{ $t("firm.audit.first-submitted-at") }}</th>
              <th>{{ $t("firm.audit.last-action-at") }}</th>
              <th>{{ $t("firm.audit.test-ready") }}</th>
              <th>{{ $t("firm.audit.prod-ready") }}</th>
              <th>{{ $t("firm.audit.completed-at") }}</th>
              <th>{{ $t("firm.audit.answered-count") }}</th>
              <th>{{ $t("firm.audit.approved-count") }}</th>
              <th></th>
              <th style="width: 140px"></th>
            </tr>
          </thead>
          <tbody v-if="pagination && pagination.docs && pagination.docs.length">
            <tr v-for="item in pagination.docs" :key="item._id">
              <td>{{ item.name | translate }}</td>
              <td>{{ formatStatus(item.status) }}</td>
              <td>{{ item.auditStartedAt | date(true) }}</td>
              <td>{{ item.submittedAt | date(true) }}</td>
              <td>{{ item.lastStatusChangedAt | date(true) }}</td>
              <td>{{ item.testReadyAt | date(true) }}</td>
              <td>{{ item.prodReadyAt | date(true) }}</td>
              <td>{{ item.completedAt | date(true) }}</td>
              <td>{{ item.answeredQuestionCount }}</td>
              <td>{{ item.approvedQuestionCount }}</td>
              <td>
                <div v-if="canViewQuestionSetDetail(item)">
                  <i class="mdi mdi-inspector"></i>
                  <router-link :to="`/fwsed/question-set/answering/${item._id}`" class="btn btn-link btn-sm p-0">
                    {{ $t("common.show") }}
                  </router-link>
                </div>
                <div v-else>
                  <p class="btn btn-sm p-0 text-muted">
                    <span v-b-tooltip.hover :title="'Yalnızca tamamlanmış veya onaya gönderilmiş soru setlerinin yanıtlarını inceleyebilirsiniz'">
                      {{ $t("common.show") }}
                    </span>
                  </p>
                </div>
              </td>
              <td>
                <i class="bx bx-trash-alt text-danger hand  mr-3" v-b-tooltip.hover :title="$t('common.delete')" @click="deleteById(item)" style="font-size: 18px; "></i>
                <!-- <i class="iconoir-trash hand" @click="deleteById(item)" style="font-size: 1.5rem; color: #99A1B7;"></i> -->
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <content-loading v-if="isDetailLoading" />

      <empty v-if="!isDetailLoading && (!pagination || !pagination.docs || !pagination.docs.length)">
        <h5 class="text-center">{{ $t("common.empty") }}</h5>
      </empty>

      <div v-if="pagination" class="row mb-5 pb-5">
        <div class="col">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="10" size="sm"></b-pagination>
          </ul>
        </div>
      </div>

      <b-modal
        id="send-question-set-modal"
        body-class="modal-body-expand"
        size="md"
        :title="$t('firm.audit.send-question-set')"
        @hide="resetSendModal"
        :ok-only="false"
        hide-footer
      >
        <div class="" style="overflow:visible !important">
          <filter-select
            key="questionSetFilter"
            id="questionSetFilter"
            ref="questionSetFilter"
            :placeholder="$t('firm.audit.question-sets-to-send')"
            :selected="selectedLibraryQuestionSets"
            :multiple="true"
            @filter="onQuestionSetSearch"
            @changed="onQuestionSetSelect"
          />
          <div class="d-flex justify-content-end mt-3">
            <b-button variant="secondary" size="sm" class="mr-2" @click="$bvModal.hide('send-question-set-modal')">{{ $t("common.cancel") }}</b-button>
            <b-button variant="primary" size="sm" :disabled="!selectedLibraryQuestionSets.length || isSending" @click="sendToFirm">
              <b-spinner v-if="isSending" small type="border" class="mr-2" />
              {{ $t("firm.audit.send-action") }}
            </b-button>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { getFirmStatus } from "@/helpers/filters";
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";
import FirmStatus from "@cc/firm-status";

export default {
  components: { ContentLoading, Empty, FirmStatus },
  props: {
    firmId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      request: {
        options: { page: 1, limit: 10, sort: { createdAt: "desc" } },
        query: {},
      },

      selectedLibraryQuestionSets: [],

      isSending: false,
      newAuditName: "",
      isCreatingAudit: false,
      isInitializing: true, // Loading state for deep link hydration
    };
  },
  async mounted() {
    // Deep link support: fetch firm and audit if not in store
    try {
      // 1. Fetch firm if not loaded OR if loaded firm ID differs from URL param
      if (this.firmId && (!this.firm || this.firm._id !== this.firmId)) {
        await this.$store.dispatch("organizationFirm/findById", this.firmId);
      }

      // 2. Fetch audit if provided in route and (not loaded OR ID differs)
      const auditId = this.$route.params.auditId;
      if (auditId && (!this.selectedAudit || this.selectedAudit._id !== auditId)) {
        await this.$store.dispatch("firmAudit/findById", auditId);
      }
    } catch (error) {
      this.$handleHttpErrorByStatusCode(error);
      // Optionally redirect back if critical data can't be loaded
      // this.$router.push('/fwsed/firm-management');
    } finally {
      this.isInitializing = false;
    }
  },
  watch: {},
  computed: {
    hasOrganizationQuestionSetViewPermission() {
      if (this.currentAccountHasPermissions(["ORGANIZATION_ADMIN", "ORGANIZATION_INFORMATION_TECHNOLOGY_AUDITOR"])) {
        return true;
      }

      return false;
    },
    isTemplateLoading() {
      return this.$store.getters["questionSet/isLoading"];
    },
    pagination() {
      return this.$store.getters["firmAudit/detailPagination"];
    },
    isDetailLoading() {
      return this.$store.getters["firmAudit/isDetailLoading"];
    },
    auditsPagination() {
      return this.$store.getters["firmAudit/audits"];
    },

    isAuditsLoading() {
      return this.$store.getters["firmAudit/isAuditsLoading"];
    },
    selectedAudit: {
      get() {
        return this.$store.getters["firmAudit/selectedEntity"];
      },
      set(value) {
        this.$store.commit("firmAudit/selectedEntity", value);
      },
    },
    firm() {
      return this.$store.getters["organizationFirm/selectedEntity"];
    },
    currentProduct() {
      return this.$store.getters["product/currentProduct"] || "FWSED";
    },
    firmStatus() {
      return getFirmStatus(this.firm, this.currentProduct);
    },
  },
  watch: {
    firmId: {
      immediate: true,
      handler() {
        this.request.options.page = 1;
      },
    },

    request: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchQuestionSets();
      },
    },
  },
  methods: {
    goBack() {
      if (!this.firm) {
        return;
      }
      this.$router.push(`/fwsed/firm-management/${this.firm._id}/audit`);
    },
    async openSendModal() {
      if (!this.selectedAudit) {
        this.$toast.warning(this.$t("firm.audit.select-audit-warning") || "Lütfen önce bir denetim seçin");
        return;
      }
      this.selectedLibraryQuestionSets = [];
      this.$bvModal.show("send-question-set-modal");
    },
    async onQuestionSetSearch(value, callback) {
      const product = this.currentProduct || "FWSED";
      const response = await this.$store.dispatch(`questionSet/paginate`, {
        options: { limit: 20 },
        query: {
          name: { value, products: { value: product, isObject: true } },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onQuestionSetSelect(value) {
      this.selectedLibraryQuestionSets = value;
    },
    canViewQuestionSetDetail(item) {
      return ["BT_REVIEW", "COMPLETED"].includes(item.status);
    },

    async deleteById(item) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`organizationQuestionSet/delete`, item._id);
        this.rightPanelClosed();

        this.$toast.success("'" + this.$options.filters.translate(item.name) + "' " + this.$t("common.delete-successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { timeout: 15000 });
      }
    },
    async fetchQuestionSets() {
      if (!this.firmId) {
        return;
      }
      await this.$store.dispatch("firmAudit/fetchFirmQuestionSets", { firmId: this.firmId, auditId: this.selectedAudit?._id, pagination: this.request });
    },
    async fetchFirmDashboardDetail() {
      if (!this.firmId) {
        return;
      }
      await this.$store.dispatch("firmAudit/fetchFirmDashboardDetail", { firmId: this.firmId, auditId: this.selectedAudit?._id });
    },
    rightPanelClosed() {
      this.fetchQuestionSets();
      // this.fetchLibraryItems();

      this.$store.dispatch("workspaceQuestionSet/resetForm");
      this.$store.dispatch("organizationQuestionSet/resetForm");
    },
    refresh() {
      this.fetchQuestionSets();
    },

    resetSendModal() {
      this.selectedsTemplateIds = [];
    },
    async sendToFirm() {
      if (!this.selectedLibraryQuestionSets.length) {
        return;
      }
      this.isSending = true;
      try {
        await this.$store.dispatch("organizationQuestionSet/sendToFirm", {
          firmId: this.firmId,
          auditId: this.selectedAudit?._id,
          libraryQuestionSetIds: this.selectedLibraryQuestionSets.map((qs) => qs._id),
        });
        this.$bvModal.hide("send-question-set-modal");
        this.$toast.success(this.$t("firm.audit.send-success"));
        await this.fetchQuestionSets();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isSending = false;
      }
    },

    formatStatus(status) {
      if (!status) {
        return "-";
      }
      const map = {
        FIRM_REVIEW: this.$t("firm.audit.status.firm-review"),
        BT_REVIEW: this.$t("firm.audit.status.bt-review"),
        COMPLETED: this.$t("firm.audit.status.completed"),
        IN_PROGRESS: this.$t("firm.audit.status.in-progress"),
        REJECTED: this.$t("firm.audit.status.rejected"),
        NONE: this.$t("firm.audit.status.none"),
      };
      return map[status] ?? status;
    },
  },
};
</script>
<style>
#send-question-set-modal .multiselect__content-wrapper {
  position: relative !important;
}
</style>

<style lang="scss" scoped>
.audit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.audit-card {
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;

  &:hover {
    border-color: #4b72fa;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &.active {
    border-color: #4b72fa;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  }

  .audit-name {
    font-weight: 600;
    margin-bottom: 6px;
  }

  .audit-meta {
    font-size: 12px;
    color: #6c757d;
    display: flex;
    justify-content: space-between;
  }
}
tr {
  text-align: center;
}
td {
  text-align: center;
}
th {
  vertical-align: middle !important;
}
</style>
